import { useState, useEffect, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useAuth } from '@/context/AuthContext';

// ── Competiciones ─────────────────────────────────────────────────────────────
const COMPETITIONS = [
  { id: 'laliga',     label: 'LaLiga',            flag: '🇪🇸', sport: 'soccer',     color: '#ef4444' },
  { id: 'premier',    label: 'Premier League',    flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', sport: 'soccer',     color: '#a78bfa' },
  { id: 'bundesliga', label: 'Bundesliga',        flag: '🇩🇪', sport: 'soccer',     color: '#f59e0b' },
  { id: 'seriea',     label: 'Serie A',           flag: '🇮🇹', sport: 'soccer',     color: '#00b4d8' },
  { id: 'ligue1',     label: 'Ligue 1',           flag: '🇫🇷', sport: 'soccer',     color: '#e879f9' },
  { id: 'champions',  label: 'Champions League',  flag: '⭐', sport: 'soccer',     color: '#ffd700' },
  { id: 'europa',     label: 'Europa League',     flag: '🏆', sport: 'soccer',     color: '#ff6b35' },
  { id: 'conference', label: 'Conference League', flag: '🌿', sport: 'soccer',     color: '#00e87a' },
  { id: 'nba',        label: 'NBA',               flag: '🏀', sport: 'basketball', color: '#ef4444' },
] as const;

type CompId = typeof COMPETITIONS[number]['id'];

// ── Tipos ─────────────────────────────────────────────────────────────────────
type DataSource = 'cache' | 'cache-stale' | 'the-odds-api' | 'the-odds-api-direct' | null;

interface OddsEvent {
  id: string;
  homeTeam: string;
  awayTeam: string;
  league: string;
  date: string;
  time: string;
  sport: string;
  homeOdds: Record<string, number>;
  drawOdds: Record<string, number>;
  awayOdds: Record<string, number>;
  overOdds: Record<string, number>;
  underOdds: Record<string, number>;
  totalsLine: number;
  bookmakers: string[];
}

// ── Sub-componentes ───────────────────────────────────────────────────────────
function BestBadge({ val, best }: { val: number; best: number }) {
  const isBest = val > 0 && val === best;
  if (val <= 0) return <span className="text-[#3d4149] text-xs">—</span>;
  return (
    <span className={`px-2.5 py-1.5 rounded-lg text-sm font-bold tabular-nums inline-block transition-all ${
      isBest
        ? 'bg-[#00e87a]/20 text-[#00e87a] border border-[#00e87a]/30'
        : 'text-white'
    }`}>
      {val.toFixed(2)}
    </span>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function ComparadorPage() {
  const { plan, isRegistered, openPaywallModal, openRegisterModal } = useAuth();
  const isPremium = plan === 'premium' || plan === 'elite';

  const [selectedComp, setSelectedComp] = useState<CompId>('laliga');
  const [events, setEvents]             = useState<OddsEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<OddsEvent | null>(null);
  const [activeMarket, setActiveMarket] = useState<'h2h' | 'totals'>('h2h');
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [lastUpdated, setLastUpdated]   = useState('');
  const [dataSource, setDataSource]     = useState<DataSource>(null);
  const [cacheExpiresAt, setCacheExpiresAt] = useState<string | null>(null);

  const comp = COMPETITIONS.find((c) => c.id === selectedComp)!;
  const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;

  const loadOdds = useCallback(async (compId: CompId) => {
    setLoading(true);
    setError(null);
    setSelectedEvent(null);
    setEvents([]);
    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/odds-comparador?sport=${compId}`);
      const data = await res.json();

      if (data.status === 'error') {
        setError(data.message ?? 'Error al cargar cuotas');
        return;
      }

      if (data.status === 'ok' && Array.isArray(data.data)) {
        setEvents(data.data);
        if (data.data.length > 0) setSelectedEvent(data.data[0]);
        setDataSource(data.source ?? null);
        setCacheExpiresAt(data.expiresAt ?? null);
        const cachedTime = data.cachedAt
          ? new Date(data.cachedAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
          : new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
        setLastUpdated(cachedTime);
      } else {
        setEvents([]);
      }
    } catch (e) {
      setError('No se pudo conectar con el servidor de cuotas.');
    } finally {
      setLoading(false);
    }
  }, [supabaseUrl]);

  useEffect(() => { loadOdds(selectedComp); }, [selectedComp, loadOdds]);

  const getBest = (oddsObj: Record<string, number>) => {
    const vals = Object.values(oddsObj).filter((v) => v > 0);
    return vals.length > 0 ? Math.max(...vals) : 0;
  };

  const formatDate = (date: string, time: string) => {
    if (!date) return '—';
    const d = new Date(`${date}T12:00:00`);
    return d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }) + ' · ' + time;
  };

  // All bookmakers present in selected event
  const allBookmakers = selectedEvent
    ? [...new Set([
        ...Object.keys(selectedEvent.homeOdds),
        ...Object.keys(selectedEvent.awayOdds),
        ...Object.keys(selectedEvent.drawOdds),
      ])]
    : [];

  // For free users show first 3, rest locked
  const visibleBMs = isPremium ? allBookmakers : allBookmakers.slice(0, 3);
  const lockedBMs  = isPremium ? [] : allBookmakers.slice(3);

  // Market rows
  const h2hRows = selectedEvent
    ? comp.sport === 'soccer'
      ? [
          { label: `1 · ${selectedEvent.homeTeam}`, odds: selectedEvent.homeOdds },
          { label: 'X · Empate',                    odds: selectedEvent.drawOdds },
          { label: `2 · ${selectedEvent.awayTeam}`, odds: selectedEvent.awayOdds },
        ]
      : [
          { label: selectedEvent.homeTeam, odds: selectedEvent.homeOdds },
          { label: selectedEvent.awayTeam, odds: selectedEvent.awayOdds },
        ]
    : [];

  const totalsRows = selectedEvent
    ? [
        { label: `Más de ${selectedEvent.totalsLine} ${comp.sport === 'basketball' ? 'pts' : 'goles'}`,   odds: selectedEvent.overOdds },
        { label: `Menos de ${selectedEvent.totalsLine} ${comp.sport === 'basketball' ? 'pts' : 'goles'}`, odds: selectedEvent.underOdds },
      ]
    : [];

  const activeRows = activeMarket === 'h2h' ? h2hRows : totalsRows;

  return (
    <div className="bg-[#0d0f14] min-h-screen">
      <Navbar />
      <div className="pt-16">

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="bg-[#151719] border-b border-[#2a2d3a]">
          <div className="max-w-[1440px] mx-auto px-6 py-10">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <p className="text-[#00e87a] text-xs font-bold tracking-widest uppercase mb-2 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00e87a] animate-pulse inline-block"></span>
                  {dataSource === 'cache' ? 'Caché activa · 0 créditos usados' : dataSource === 'cache-stale' ? 'Caché desactualizada · actualizando...' : 'Cuotas reales · The Odds API'}
                </p>
                <h1 className="text-white text-4xl font-black uppercase">Comparador de Cuotas</h1>
                <p className="text-[#a8b2c1] text-sm mt-2">
                  LaLiga · Premier · Bundesliga · Serie A · Ligue 1 · Champions · Europa · Conference · NBA
                </p>
              </div>
              <div className="flex items-center gap-3">
                {lastUpdated && (
                  <span className="text-[#6b7280] text-xs flex items-center gap-1">
                    <i className="ri-time-line text-xs"></i>
                    {lastUpdated}
                    {cacheExpiresAt && (
                      <span className="ml-1 text-[#3d4149]">
                        · expira {new Date(cacheExpiresAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </span>
                )}
                <button
                  onClick={() => loadOdds(selectedComp)}
                  disabled={loading}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#1e2028] border border-[#2a2d3a] text-[#a8b2c1] text-xs font-semibold hover:text-white transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
                >
                  <i className={`ri-refresh-line text-xs ${loading ? 'animate-spin' : ''}`}></i>
                  Actualizar
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Competition tabs ────────────────────────────────────────── */}
        <div className="border-b border-[#2a2d3a] bg-[#151719]">
          <div className="max-w-[1440px] mx-auto px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-2">
              {COMPETITIONS.map((c) => (
                <button
                  key={c.id}
                  onClick={() => { setSelectedComp(c.id); setActiveMarket('h2h'); }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                    selectedComp === c.id ? 'text-black font-black' : 'text-[#6b7280] hover:text-white hover:bg-white/5'
                  }`}
                  style={selectedComp === c.id ? { background: c.color } : undefined}
                >
                  <span className="text-base">{c.flag}</span>
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-[1440px] mx-auto px-6 py-8">

          {/* Loading */}
          {loading && (
            <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-16 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl mx-auto mb-3" style={{ background: `${comp.color}20` }}>
                <i className="ri-refresh-line text-xl animate-spin" style={{ color: comp.color }}></i>
              </div>
              <p className="text-[#6b7280] text-sm">Cargando cuotas reales de {comp.label}...</p>
              <p className="text-[#3d4149] text-xs mt-1">Conectando con The Odds API</p>
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="bg-[#ef4444]/10 border border-[#ef4444]/30 rounded-xl px-5 py-5 flex items-start gap-3">
              <i className="ri-error-warning-line text-[#ef4444] text-lg mt-0.5 flex-shrink-0"></i>
              <div>
                <p className="text-[#ef4444] font-bold text-sm">Error al cargar cuotas</p>
                <p className="text-[#a8b2c1] text-xs mt-1">{error}</p>
                <button onClick={() => loadOdds(selectedComp)} className="mt-2 text-[#ef4444] text-xs underline cursor-pointer whitespace-nowrap">
                  Reintentar
                </button>
              </div>
            </div>
          )}

          {/* No events */}
          {!loading && !error && events.length === 0 && (
            <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-16 text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/5 mx-auto mb-3">
                <i className="ri-calendar-event-line text-[#6b7280] text-2xl"></i>
              </div>
              <p className="text-white font-semibold">Sin partidos disponibles en las proximas 24h</p>
              <p className="text-[#6b7280] text-sm mt-1">
                No hay cuotas disponibles para {comp.label} en este momento. Prueba otra competición.
              </p>
            </div>
          )}

          {/* Main content */}
          {!loading && !error && events.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

              {/* ── Match list ─────────────────────────────────────── */}
              <div className="lg:col-span-1">
                <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-[#2a2d3a] flex items-center justify-between">
                    <p className="text-[#6b7280] text-xs font-bold uppercase tracking-wide">
                      Partidos ({events.length})
                    </p>
                    <span className="flex items-center gap-1 text-[#00e87a] text-[10px] font-bold">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00e87a] animate-pulse inline-block"></span>
                      En vivo
                    </span>
                  </div>
                  <div className="divide-y divide-[#1e2028] max-h-[680px] overflow-y-auto">
                    {events.map((ev) => {
                      const bestHome = getBest(ev.homeOdds);
                      return (
                        <button
                          key={ev.id}
                          onClick={() => { setSelectedEvent(ev); setActiveMarket('h2h'); }}
                          className={`w-full text-left px-4 py-3.5 transition-all cursor-pointer ${
                            selectedEvent?.id === ev.id
                              ? 'border-l-2 bg-white/[0.03]'
                              : 'hover:bg-white/[0.02]'
                          }`}
                          style={selectedEvent?.id === ev.id ? { borderLeftColor: comp.color } : undefined}
                        >
                          <p className="text-[#6b7280] text-[10px] font-bold uppercase tracking-wide mb-1.5 truncate">
                            {formatDate(ev.date, ev.time)}
                          </p>
                          <p className="text-white text-xs font-semibold truncate">{ev.homeTeam}</p>
                          <p className="text-[#a8b2c1] text-xs truncate mt-0.5">vs {ev.awayTeam}</p>
                          {bestHome > 0 && (
                            <div className="flex items-center gap-1.5 mt-2">
                              <span className="text-[#00e87a] text-[10px] font-bold">Mejor:</span>
                              <span className="text-white text-[10px] font-black tabular-nums">{bestHome.toFixed(2)}</span>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* ── Odds table ─────────────────────────────────────── */}
              <div className="lg:col-span-3">
                {selectedEvent && (
                  <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl overflow-hidden">

                    {/* Match header */}
                    <div className="px-6 py-5 border-b border-[#2a2d3a]">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                        <div>
                          <p className="text-[#6b7280] text-xs mb-1">{selectedEvent.league} · {formatDate(selectedEvent.date, selectedEvent.time)}</p>
                          <div className="flex items-center gap-3">
                            <span className="text-white font-black text-lg">{selectedEvent.homeTeam}</span>
                            <span className="text-[#3d4149] font-black text-sm">vs</span>
                            <span className="text-white font-black text-lg">{selectedEvent.awayTeam}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#00e87a]/10 border border-[#00e87a]/20 text-[#00e87a] text-xs font-bold">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#00e87a] animate-pulse inline-block"></span>
                            Cuotas reales
                          </span>
                          <span className="px-3 py-1.5 rounded-full bg-[#1e2028] text-[#6b7280] text-xs font-bold">
                            {allBookmakers.length} casas
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Market tabs */}
                    <div className="flex items-center gap-1 px-4 py-3 border-b border-[#1e2028]">
                      {[
                        { id: 'h2h',    label: comp.sport === 'basketball' ? 'Ganador' : '1X2 — Resultado' },
                        { id: 'totals', label: comp.sport === 'basketball' ? `Más/Menos ${selectedEvent.totalsLine} pts` : `Más/Menos ${selectedEvent.totalsLine} goles` },
                      ].map((m) => (
                        <button
                          key={m.id}
                          onClick={() => setActiveMarket(m.id as 'h2h' | 'totals')}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                            activeMarket === m.id ? 'text-black font-black' : 'text-[#6b7280] hover:text-white hover:bg-white/5'
                          }`}
                          style={activeMarket === m.id ? { background: comp.color } : undefined}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>

                    {/* Odds grid */}
                    {activeRows.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[520px]">
                          <thead>
                            <tr className="border-b border-[#1e2028]">
                              <th className="px-6 py-3 text-left text-[#6b7280] text-[10px] font-bold uppercase tracking-wider">Selección</th>
                              {visibleBMs.map((bm) => (
                                <th key={bm} className="px-3 py-3 text-center text-[#a8b2c1] text-xs font-bold whitespace-nowrap">{bm}</th>
                              ))}
                              {lockedBMs.map((bm) => (
                                <th key={bm} className="px-3 py-3 text-center">
                                  <div className="flex items-center justify-center gap-1 text-[#3d4149] text-xs whitespace-nowrap">
                                    <i className="ri-lock-line text-xs"></i>
                                    <span>{bm}</span>
                                  </div>
                                </th>
                              ))}
                              <th className="px-4 py-3 text-center text-[#00e87a] text-xs font-bold">MÁXIMO</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activeRows.map((row) => {
                              const best = getBest(row.odds);
                              return (
                                <tr key={row.label} className="border-b border-[#1e2028]/50 hover:bg-white/[0.02]">
                                  <td className="px-6 py-4 text-[#a8b2c1] text-sm font-medium whitespace-nowrap">{row.label}</td>
                                  {visibleBMs.map((bm) => (
                                    <td key={bm} className="px-3 py-4 text-center">
                                      <BestBadge val={row.odds[bm] ?? 0} best={best} />
                                    </td>
                                  ))}
                                  {lockedBMs.map((bm) => (
                                    <td key={bm} className="px-3 py-4 text-center">
                                      <div className="w-full h-8 bg-[#1e2028]/60 rounded flex items-center justify-center">
                                        <i className="ri-lock-line text-[#3d4149] text-xs"></i>
                                      </div>
                                    </td>
                                  ))}
                                  <td className="px-4 py-4 text-center">
                                    {best > 0
                                      ? <span className="text-[#00e87a] font-black text-base tabular-nums">{best.toFixed(2)}</span>
                                      : <span className="text-[#3d4149] text-xs">—</span>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="p-8 text-center">
                        <p className="text-[#6b7280] text-sm">Sin cuotas para este mercado.</p>
                      </div>
                    )}

                    {/* Footer */}
                    <div className="px-6 py-3 bg-[#0d0f14]/50 flex items-center justify-between text-xs text-[#3d4149] flex-wrap gap-2">
                      <span>Cuotas reales · Fuente: The Odds API · Mejor cuota en verde</span>
                      {!isPremium && lockedBMs.length > 0 && (
                        <button
                          onClick={() => isRegistered ? openPaywallModal('premium') : openRegisterModal()}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#ffd700]/10 border border-[#ffd700]/20 text-[#ffd700] text-xs font-bold cursor-pointer hover:bg-[#ffd700]/20 transition-all whitespace-nowrap"
                        >
                          <i className="ri-lock-line text-xs"></i>
                          Ver {lockedBMs.length} casas más — Premium
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Premium upsell */}
                {!isPremium && allBookmakers.length > 3 && (
                  <div className="mt-4 bg-gradient-to-r from-[#ffd700]/5 to-[#ff6b35]/5 border border-[#ffd700]/20 rounded-xl px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#ffd700]/15 text-[#ffd700] flex-shrink-0 mt-0.5">
                        <i className="ri-vip-crown-line text-base"></i>
                      </div>
                      <div>
                        <p className="text-white font-bold text-sm">{lockedBMs.length} casas bloqueadas</p>
                        <p className="text-[#a8b2c1] text-xs mt-0.5">
                          Con Premium ves <strong className="text-[#ffd700]">todas las casas disponibles</strong> y siempre encuentras la mejor cuota real.
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => isRegistered ? openPaywallModal('premium') : openRegisterModal()}
                      className="flex items-center gap-2 px-4 py-2 bg-[#ffd700] text-black text-xs font-black rounded-xl cursor-pointer whitespace-nowrap hover:bg-[#f0c800] transition-all flex-shrink-0"
                    >
                      <i className="ri-vip-crown-line text-sm"></i>
                      Activar Premium — 2,50€/mes
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
